import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import React, { useState } from "react";
import CustomLabel from "./common/CustomLabel";
import CustomTextField from "./common/CustomTextField";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";

interface FileUploadFieldProps {
  placeholder?: string;
  accept?: string;
  onFileChange: (file: File | null) => void;
  containerStyles?: object;
  showQuestionIcon?: boolean;
  questionTooltipText?: string;
  title?: string;
  popupImageUrl?: string;
  popupImageAlt?: string;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  placeholder = "Add Excel sheet",
  accept = ".xlsx, .xls",
  onFileChange,
  containerStyles = {},
  showQuestionIcon = false,
  questionTooltipText,
  title,
  popupImageUrl,
  popupImageAlt, 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [openPopup, setOpenPopup] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    onFileChange(selectedFile);
  };

  return (
    <Box sx={{ ...containerStyles }}>
      {title && (
        <Box display="flex" alignItems="center">
          <CustomLabel text={title} fontWeight={500} fontSize="19px" />
          <Typography variant="body1" color="error" ml={0.5}>
            *
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: "4px", sm: "8px" },
          width: "100%",
          minWidth: 0, // Prevent overflow
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            background: "#EDEDED",
            borderRadius: "12px",
            height: { xs: "36px", sm: "40px" },
            paddingRight: { xs: "4px", sm: "8px" },
            flex: 1,
            minWidth: 0, // Prevent overflow
            overflow: "hidden",
          }}
        >
          <Button
            component="label"
            variant="contained"
            disableElevation
            sx={{
              height: { xs: "28px", sm: "32px" },
              padding: { xs: "0 8px", sm: "0 14px" },
              fontSize: { xs: "11px", sm: "12px" },
              fontWeight: 400,
              color: "#3C3C4399",
              marginLeft: { xs: 0.5, sm: 1 },
              background: "white",
              borderRadius: { xs: "8px", sm: "12px" },
              minWidth: { xs: "60px", sm: "auto" },
              flexShrink: 0, // Prevent button from shrinking
              "&:hover": {
                backgroundColor: "#D1D1D1",
              },
            }}
          >
            Browse...
            <input
              type="file"
              accept={accept}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </Button>

          <CustomTextField
            variant="outlined"
            placeholder={placeholder}
            value={file?.name || ""}
            sx={{
              background: "#EDEDED",
              border: "none",
              flex: 1,
              marginLeft: { xs: "4px", sm: "8px" },
              minWidth: 0, // Prevent overflow
              width: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#EDEDED",
                fontSize: { xs: "0.875rem", sm: "1rem" },
                "& fieldset": { border: "none" },
                "& input": {
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  padding: { xs: "8px 6px", sm: "8px 12px" },
                },
              },
            }}
            InputProps={{ 
              readOnly: true,
              sx: {
                height: { xs: "36px", sm: "40px" },
              }
            }}
          />
        </Box>

        {showQuestionIcon && (
          <>
            <Tooltip title={questionTooltipText || "Click for example"} arrow>
              <IconButton edge="end" onClick={() => setOpenPopup(true)}>
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
            <Dialog
              open={openPopup}
              onClose={() => setOpenPopup(false)}
              maxWidth="xl"
            >
              <DialogTitle sx={{ m: 0, p: 2 }}>
                Example Screenshot
                <IconButton
                  aria-label="close"
                  onClick={() => setOpenPopup(false)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Box
                  component="img"
                  src={popupImageUrl}
                  alt={popupImageAlt || "Example screenshot"}
                  sx={{
                    width: "auto",
                    maxWidth: "none",
                    maxHeight: "80vh",
                    objectFit: "contain",
                    transition: "transform 0.3s ease",
                 
                  }}
                />
              </DialogContent>
            </Dialog>
          </>
        )}
      </Box>
    </Box>
  );
};

export default FileUploadField;
